import { CheckCircle2, Plus, Sparkles } from "lucide-react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";
import { cn } from "@v2/lib/utils";
import { Button } from "@v2/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@v2/components/ui/card";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@v2/components/ui/dropdown-menu";
import { supabaseV2 } from "../../lib/supabase";
import { usePermissions } from "../../lib/permissions";
import { fixEaText } from "../../lib/ea-name";
import { EmptyState, SectionLoading } from "../../components/state";

export function EaClubSection({ teamId, eaClubId }: { teamId: string; eaClubId: string | null }) {
  const { t } = useTranslation("mondofc");
  const queryClient = useQueryClient();
  const { canManage } = usePermissions();
  const clubQuery = useQuery({
    queryKey: ["ea-club", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("latest_ea_club", { _team_id: teamId });
      if (error) throw error;
      return (data as any) || null;
    },
    enabled: !!eaClubId,
  });
  const membersQuery = useQuery({
    queryKey: ["ea-members", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2.rpc("list_ea_members", { _team_id: teamId });
      if (error) throw error;
      return (data || []) as Array<{ ea_persona: string; ea_name: string | null; data: any; fetched_at: string }>;
    },
    enabled: !!eaClubId,
  });
  const rosterQuery = useQuery({
    queryKey: ["ea-roster", teamId],
    queryFn: async () => {
      const { data, error } = await supabaseV2
        .from("players")
        .select("id, display_name, ea_sports_id, ea_account_handle")
        .eq("team_id", teamId)
        .order("display_name");
      if (error) throw error;
      return (data || []) as Array<{ id: string; display_name: string; ea_sports_id: string | null; ea_account_handle: string | null }>;
    },
    enabled: !!eaClubId,
  });

  const addToRoster = useMutation({
    mutationFn: async ({ eaPersona, eaName }: { eaPersona: string; eaName: string | null }) => {
      const name = (eaName || eaPersona).trim();
      const payload = {
        team_id: teamId,
        first_name: name,
        last_name: "",
        ea_account_handle: eaPersona,
        status: "active",
        is_guest: false,
      };
      const { error } = await supabaseV2.from("players").insert(payload);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ea-roster", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
    },
    onError: (e: any) => alert(t("clubSection.errorPrefix", { message: e.message || String(e) })),
  });

  const linkExisting = useMutation({
    mutationFn: async ({ playerId, eaPersona }: { playerId: string; eaPersona: string }) => {
      const { error } = await supabaseV2
        .from("players")
        .update({ ea_account_handle: eaPersona })
        .eq("id", playerId);
      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["ea-roster", teamId] });
      queryClient.invalidateQueries({ queryKey: ["players", teamId] });
    },
    onError: (e: any) => alert(t("clubSection.errorPrefix", { message: e.message || String(e) })),
  });

  if (!eaClubId) return null;

  const club = clubQuery.data;
  // Schema EA snapshot: { clubInfo, overallStats, membersCurrentStats, membersCareersStats }
  const overall: any = club?.overallStats || null;
  const info: any = club?.clubInfo || null;
  const members = membersQuery.data || [];
  const roster = rosterQuery.data || [];
  // Indice case-insensitive su ea_account_handle (campo dedicato al match Pro Clubs)
  // E come fallback su ea_sports_id (alcuni founder hanno messo qui l'handle in passato).
  const rosterByKey = new Map<string, { id: string; display_name: string }>();
  for (const r of roster) {
    if (r.ea_account_handle)
      rosterByKey.set(r.ea_account_handle.toLowerCase(), { id: r.id, display_name: r.display_name });
    else if (r.ea_sports_id)
      rosterByKey.set(r.ea_sports_id.toLowerCase(), { id: r.id, display_name: r.display_name });
  }
  const findLinked = (m: any) => {
    const candidates = [m.ea_persona, m.data?.name, m.data?.proName].filter(Boolean) as string[];
    for (const c of candidates) {
      const hit = rosterByKey.get(String(c).toLowerCase());
      if (hit) return hit;
    }
    return null;
  };
  // Mappa playerId → handle del membro EA a cui è linkato (se ce n'è uno).
  // Mostriamo TUTTI i player rosa nel dropdown, indicando accanto ai linkati a chi.
  const playerLinkedTo = new Map<string, string>();
  for (const m of members) {
    const hit = findLinked(m);
    if (hit) playerLinkedTo.set(hit.id, m.ea_persona);
  }
  // Ordinamento: prima i non linkati (alfabetico), poi i linkati (alfabetico).
  const dropdownRoster = [...roster].sort((a, b) => {
    const aLinked = playerLinkedTo.has(a.id) ? 1 : 0;
    const bLinked = playerLinkedTo.has(b.id) ? 1 : 0;
    if (aLinked !== bLinked) return aLinked - bLinked;
    return a.display_name.localeCompare(b.display_name);
  });

  return (
    <Card>
      <CardHeader className="pb-3">
        <CardTitle className="text-lg flex items-center gap-2">
          {t("clubSection.title")}
        </CardTitle>
        <CardDescription>
          {fixEaText(info?.name) || t("clubSection.clubFallback", { id: eaClubId })}
          {overall ? (
            <>
              {" · "}
              <span className="font-medium text-foreground">
                {overall.wins}V {overall.ties || 0}N {overall.losses}P
              </span>
              {" · "}
              {overall.skillRating ? `SR ${overall.skillRating}` : ""}
            </>
          ) : null}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-3">
        {clubQuery.isLoading && <SectionLoading />}
        {!clubQuery.isLoading && !club && (
          <EmptyState text={t("clubSection.noData")} />
        )}
        {overall && (
          <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-3">
            <div className="pcell text-foreground">
              <span className="stat-label">{t("clubSection.skillRating")}</span>
              <span className="stat-value">{overall.skillRating || "-"}</span>
            </div>
            <div className="pcell">
              <span className="stat-label">{t("clubSection.matches")}</span>
              <span className="stat-value">{overall.gamesPlayed || 0}</span>
            </div>
            <div className="pcell pcell-ok">
              <span className="stat-label">{t("clubSection.wins")}</span>
              <span className="stat-value">{overall.wins || 0}</span>
            </div>
            <div className="pcell pcell-warn">
              <span className="stat-label">{t("clubSection.draws")}</span>
              <span className="stat-value">{overall.ties || 0}</span>
            </div>
            <div className="pcell pcell-danger">
              <span className="stat-label">{t("clubSection.losses")}</span>
              <span className="stat-value">{overall.losses || 0}</span>
            </div>
            <div className="pcell">
              <span className="stat-label">{t("clubSection.goalDiff")}</span>
              {(() => {
                const gf = Number(overall.goals || 0);
                const ga = Number(overall.goalsAgainst || 0);
                const diff = gf - ga;
                const sign = diff > 0 ? "+" : diff < 0 ? "−" : "";
                return (
                  <>
                    <span className={cn("stat-value tabular-nums", diff > 0 && "text-[hsl(var(--ok))]", diff < 0 && "text-[hsl(var(--danger))]")}>
                      {sign}{Math.abs(diff)}
                    </span>
                    <span className="stat-hint tabular-nums">{gf}/{ga}</span>
                  </>
                );
              })()}
            </div>
          </div>
        )}
        {members.length > 0 && (
          <div>
            <p className="eb-eyebrow mb-2">{t("clubSection.members", { count: members.length })}</p>
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="text-muted-foreground">
                  <tr>
                    <th className="text-left eb-data text-[13px] py-1 pr-2">{t("clubSection.thAccount")}</th>
                    <th className="text-right eb-data text-[13px] px-1">{t("clubSection.thPT")}</th>
                    <th className="text-right eb-data text-[13px] px-1">{t("clubSection.thG")}</th>
                    <th className="text-right eb-data text-[13px] px-1">{t("clubSection.thA")}</th>
                    <th className="text-right eb-data text-[13px] px-1">{t("clubSection.thRating")}</th>
                    <th className="text-right eb-data text-[13px] px-1">{t("clubSection.thWinPct")}</th>
                    {canManage && (
                      <th className="text-right eb-data text-[13px] px-1">{t("clubSection.thRoster")}</th>
                    )}
                  </tr>
                </thead>
                <tbody>
                  {members.map((m) => {
                    const d = m.data || {};
                    const games = Number(d.gamesPlayed) || 0;
                    const winRateRaw = Number(d.winRate);
                    const winRate = !Number.isNaN(winRateRaw) ? winRateRaw : 0;
                    const linked = findLinked(m);
                    const accountHandle = m.ea_persona;
                    const proName = d.proName || m.ea_name;
                    return (
                      <tr key={m.ea_persona} className="border-t">
                        <td className="py-1.5 pr-2 truncate">
                          {linked ? (
                            <>
                              <span className="font-medium">{linked.display_name}</span>
                              <span className="text-xs text-muted-foreground font-mono ml-2">
                                {accountHandle}
                                {proName && proName !== accountHandle ? ` · ${proName}` : ""}
                              </span>
                            </>
                          ) : (
                            <>
                              <span className="font-medium font-mono">{accountHandle}</span>
                              {proName && proName !== accountHandle && (
                                <span className="text-xs text-muted-foreground ml-2">{t("clubSection.proLabel", { name: proName })}</span>
                              )}
                            </>
                          )}
                        </td>
                        <td className="text-right font-tabular px-1">{games || "-"}</td>
                        <td className="text-right font-tabular px-1 font-semibold">{d.goals || 0}</td>
                        <td className="text-right font-tabular px-1">{d.assists || 0}</td>
                        <td className="text-right font-tabular px-1">
                          {d.ratingAve ? Number(d.ratingAve).toFixed(2) : "-"}
                        </td>
                        <td className="text-right font-tabular px-1 text-muted-foreground">{winRate}%</td>
                        {canManage && (
                          <td className="text-right font-tabular px-1">
                            {linked ? (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <button
                                    className="text-xs text-emerald-700 inline-flex items-center gap-1 hover:underline"
                                    title={t("clubSection.linkedTitle", { name: linked.display_name })}
                                  >
                                    <CheckCircle2 className="h-3 w-3" />
                                    {linked.display_name}
                                  </button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64 max-h-[60vh] overflow-y-auto">
                                  <p className="px-2 py-1 text-xs uppercase tracking-wider text-muted-foreground">
                                    {t("clubSection.reassignTitle")}
                                  </p>
                                  {dropdownRoster.map((r) => {
                                    const linkedHandle = playerLinkedTo.get(r.id);
                                    const isCurrent = linkedHandle === m.ea_persona;
                                    return (
                                      <DropdownMenuItem
                                        key={r.id}
                                        disabled={isCurrent}
                                        onClick={() =>
                                          linkExisting.mutate({ playerId: r.id, eaPersona: m.ea_persona })
                                        }
                                        className="flex items-center justify-between gap-2"
                                      >
                                        <span className="truncate">{r.display_name}</span>
                                        {isCurrent ? (
                                          <span className="text-[11px] text-emerald-600 font-mono">{t("clubSection.current")}</span>
                                        ) : linkedHandle ? (
                                          <span className="text-[11px] text-amber-600 font-mono truncate" title={t("clubSection.alreadyLinkedHover", { handle: linkedHandle })}>
                                            → {linkedHandle}
                                          </span>
                                        ) : null}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                  {dropdownRoster.length === 0 && (
                                    <p className="px-2 py-1 text-xs text-muted-foreground italic">
                                      {t("clubSection.noRoster")}
                                    </p>
                                  )}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            ) : (
                              <DropdownMenu>
                                <DropdownMenuTrigger asChild>
                                  <Button size="sm" variant="outline" className="h-6 text-xs">
                                    {t("clubSection.addToRoster")}
                                  </Button>
                                </DropdownMenuTrigger>
                                <DropdownMenuContent align="end" className="w-64 max-h-[60vh] overflow-y-auto">
                                  <DropdownMenuItem
                                    onClick={() => addToRoster.mutate({ eaPersona: m.ea_persona, eaName: m.ea_name })}
                                  >
                                    <Plus className="h-3 w-3" />
                                    {t("clubSection.createNew", { name: m.ea_name || m.ea_persona })}
                                  </DropdownMenuItem>
                                  {dropdownRoster.length > 0 && <DropdownMenuSeparator />}
                                  {dropdownRoster.length > 0 && (
                                    <p className="px-2 py-1 text-xs uppercase tracking-wider text-muted-foreground">
                                      {t("clubSection.associateExisting")}
                                    </p>
                                  )}
                                  {dropdownRoster.map((r) => {
                                    const linkedHandle = playerLinkedTo.get(r.id);
                                    return (
                                      <DropdownMenuItem
                                        key={r.id}
                                        onClick={() =>
                                          linkExisting.mutate({ playerId: r.id, eaPersona: m.ea_persona })
                                        }
                                        className="flex items-center justify-between gap-2"
                                      >
                                        <span className="truncate">{r.display_name}</span>
                                        {linkedHandle && (
                                          <span className="text-[11px] text-amber-600 font-mono truncate" title={t("clubSection.alreadyLinkedHover", { handle: linkedHandle })}>
                                            → {linkedHandle}
                                          </span>
                                        )}
                                      </DropdownMenuItem>
                                    );
                                  })}
                                </DropdownMenuContent>
                              </DropdownMenu>
                            )}
                          </td>
                        )}
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
